import React from 'react';
import { Plus, Minus, ShoppingCart, Tag } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { toast } from '../hooks/use-toast';

const ProductCard = ({ product }) => {
  const { cart, addItem, updateQty } = useCart();
  const inCart = cart.items.find((i) => i.product_id === product.product_id);
  const qty = inCart?.qty || 0;
  const onSale = product.compare_at_price && product.compare_at_price > product.price;
  const discount = onSale ? Math.round((1 - product.price / product.compare_at_price) * 100) : 0;

  const handleAdd = async () => {
    await addItem(product, 1);
    toast({ title: 'Agregado al carrito', description: product.title });
  };

  return (
    <div className="group relative bg-neutral-950 rounded-2xl border border-white/8 hover:border-orange-500/50 hover:shadow-[0_10px_35px_-10px_rgba(249,115,22,0.35)] transition-all duration-300 overflow-hidden flex flex-col">
      <Link to={`/producto/${product.handle}`} className="relative block bg-neutral-900 aspect-square overflow-hidden">
        {/* Sale badge — top left */}
        {onSale && (
          <span className="absolute top-3 left-3 z-10 bg-orange-500 text-black text-xs font-black px-2 py-1 rounded-md shadow">
            -{discount}%
          </span>
        )}
        {/* Status badges — top right, stacked by priority */}
        <div className="absolute top-3 right-3 z-10 flex flex-col items-end gap-1">
          {product.stock === 0 || !product.available ? (
            <span className="bg-red-600 text-white text-[10px] font-black px-2 py-0.5 rounded-md shadow" data-testid="badge-agotado">
              Agotado
            </span>
          ) : null}
          {product.is_restock && (
            <span className="bg-yellow-400 text-black text-[10px] font-black px-2 py-0.5 rounded-md shadow" data-testid="badge-proximamente">
              Próximamente
            </span>
          )}
          {product.is_new && !product.is_restock && (
            <span className="bg-orange-500 text-black text-[10px] font-black px-2 py-0.5 rounded-md shadow" data-testid="badge-nuevo">
              Nuevo
            </span>
          )}
        </div>
        {product.main_image ? (
          <img
            src={product.main_image}
            alt={product.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20">
            <Tag className="w-10 h-10" />
          </div>
        )}
      </Link>
      <div className="p-4 flex-1 flex flex-col">
        <p className="text-[11px] font-bold text-orange-500 uppercase tracking-wider">{product.vendor}</p>
        <Link to={`/producto/${product.handle}`}>
          <h4 className="mt-1 text-sm md:text-[15px] font-semibold text-white leading-snug line-clamp-2 min-h-[40px] group-hover:text-orange-400 transition-colors uppercase">
            {product.title}
          </h4>
        </Link>
        <div className="mt-2 flex items-baseline gap-2">
          <span className="text-xl font-black text-white">${product.price?.toLocaleString('es-MX', { minimumFractionDigits: 0 })}</span>
          {onSale && (
            <span className="text-sm text-white/40 line-through">${product.compare_at_price?.toLocaleString('es-MX')}</span>
          )}
        </div>
        {Array.isArray(product.bulk_tiers) && product.bulk_tiers.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1" data-testid="product-bulk-tiers">
            {[...product.bulk_tiers].sort((a, b) => a.qty - b.qty).slice(0, 2).map((t) => (
              <span
                key={t.qty}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-orange-500/15 text-orange-400 border border-orange-500/30"
              >
                {t.qty}x ${Number(t.price).toLocaleString('es-MX', { minimumFractionDigits: 0 })}
              </span>
            ))}
          </div>
        )}
        <div className="mt-4">
          {qty === 0 ? (
            <button
              onClick={handleAdd}
              disabled={!product.available}
              className="w-full h-10 rounded-full bg-white text-black font-semibold flex items-center justify-center gap-2 hover:bg-orange-500 active:scale-[0.98] transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ShoppingCart className="w-4 h-4" />
              {product.available ? 'Agregar' : 'Agotado'}
            </button>
          ) : (
            <div className="flex items-center justify-between rounded-full bg-orange-500 text-black px-1 h-10">
              <button onClick={() => updateQty(product.product_id, qty - 1)} className="w-8 h-8 rounded-full bg-black/15 hover:bg-black/25 flex items-center justify-center">
                <Minus className="w-4 h-4" />
              </button>
              <span className="font-black">{qty}</span>
              <button onClick={() => updateQty(product.product_id, qty + 1)} disabled={product.stock != null && qty >= product.stock} className="w-8 h-8 rounded-full bg-black/15 hover:bg-black/25 flex items-center justify-center disabled:opacity-40 disabled:cursor-not-allowed">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
